import { filter } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  Card,
  Table,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  TablePagination,
  IconButton,
  Typography,
  Backdrop,
  CircularProgress,
  Stack,
  styled,
  Button
} from '@material-ui/core';

import moment from 'moment';
import NoteAddIcon from '@material-ui/icons/NoteAdd';
import CreateIcon from '@material-ui/icons/Create';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import { baseUrl } from '../../utils/constants';
import SearchNotFound from '../SearchNotFound';
import { ClientListHead } from '../_dashboard/clients';
import Controls from '../controls/Controls';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover
  },
  '&:last-child td, &:last-child th': {
    border: 0
  }
}));

const TABLE_HEAD = [
  { id: 'billNo', label: 'Ref #', alignRight: true },
  { id: 'billDate', label: 'Period', alignRight: true },
  { id: 'description', label: 'File Name', alignRight: false },
  { id: 'symbol', label: '# of Errors', alignRight: true },
  { id: 'entno', label: '# of Entries', alignRight: true },
  { id: 'type', label: 'Total Amount', alignRight: true },
  { id: 'curr', label: 'Currency', alignRight: false },
  { id: 'creaetd', label: 'State', alignRight: false },
  { id: 'as', label: '' },
  { id: 'asd', label: '' }
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_doc) => _doc.documentType.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function ReceiptFile() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [dataList, setDataList] = useState([]);
  const [orderBy, setOrderBy] = useState('documentType');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    subTitle: '',
    heading: '',
    color: '#000000'
  });
  const navigate = useNavigate();

  useEffect(() => {
    getSsbFileReport();
    setSelected([]);
    setFilterName('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getSsbFileReport = async () => {
    setLoading(true);
    fetch(`${baseUrl}/api/ssb/GetSsbFileReport`)
      .then((res) => res.json())
      .then(
        (result) => {
          setDataList(result.result);
          setLoading(false);
        },
        (error) => {
          notifyPopup(`Error in Retrieving GetSsbFileReport!: ${error}`, 'error');
        }
      );
  };

  const handleBatchDelete = async (e) => {
    if (e === '') {
      return;
    }
    setConfirmDialog({
      ...confirmDialog,
      isOpen: false
    });
    setLoading(true);
    fetch(`${baseUrl}/api/ssb/DeleteSsbBatch?refId=${e.refId}&type=Normal`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    })
      .then((res) => res.json())
      .then(
        (result) => {
          setLoading(false);
          if (result.replyStatus === 1) {
            notifyPopup(`Error in Deleting Batch!: ${result.replyMessage}`, 'error');
          } else {
            notifyPopup(`Batch Deleted Successfully!`, 'success');
            navigate('/');
          }
        },
        (error) => {
          notifyPopup(`Error in Deleting Batch!: ${error}`, 'error');
        }
      );
  };

  const getStatus = (e) => {
    switch (e.processState) {
      case 0:
        return 'Created';
      case 1:
        return 'Matched Ids';
      case 2:
        return 'Complete';
      default:
        return 'Created';
    }
  };

  const openNew = () => {
    navigate(`/ssb/bulkbilldetails`);
  };

  const handleEditView = (e) => {
    openEdit(e.row.refId);
  };

  const openEdit = (e) => {
    navigate(`/ssb/processfile/${e}`);
  };

  const handleLoadingClose = () => {
    setLoading(false);
  };

  const notifyPopup = (notifyMessage, notifyType) => {
    setNotify({
      isOpen: true,
      message: `${notifyMessage}`,
      type: `${notifyType}`
    });
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - dataList.length) : 0;

  const filteredUsers = applySortFilter(dataList, getComparator(order, orderBy), filterName);

  const isUserNotFound = filteredUsers.length === 0;

  return (
    <Container>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb="10">
        <Typography component="span" variant="h6" gutterBottom>
          SSB Receipt Files
        </Typography>
        <Button
          type="button"
          variant="outlined"
          color="secondary"
          startIcon={<NoteAddIcon />}
          onClick={() => openNew()}
        >
          New Receipt File
        </Button>
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb="10">
        <Typography component="span" variant="h6" gutterBottom>
          ..................
        </Typography>
      </Stack>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
        onClick={handleLoadingClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Card>
        <TableContainer sx={{ minWidth: 800 }}>
          <Table size="small" aria-label="a dense table">
            <ClientListHead
              order={order}
              orderBy={orderBy}
              headLabel={TABLE_HEAD}
              rowCount={dataList.length}
              numSelected={selected.length}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => {
                  /* eslint-disable camelcase */
                  const {
                    refId,
                    uploadDate,
                    ssbFileName,
                    noOfEntries,
                    totalAmount,
                    totalErrors,
                    currency,
                    processState
                  } = row;
                  const isItemSelected = selected.indexOf(refId) !== -1;

                  return (
                    <StyledTableRow
                      hover
                      key={refId}
                      tabIndex={-1}
                      role="checkbox"
                      selected={isItemSelected}
                      aria-checked={isItemSelected}
                    >
                      <TableCell padding="checkbox" />
                      <TableCell align="right">{refId}</TableCell>
                      <TableCell align="right" component="th" scope="row" padding="none">
                        {moment(uploadDate).isValid()
                          ? moment(uploadDate).format('YYYY-MM-DD')
                          : ''}
                      </TableCell>
                      <TableCell align="left">{ssbFileName}</TableCell>
                      <TableCell align="right">{totalErrors}</TableCell>
                      <TableCell align="right">{noOfEntries}</TableCell>
                      <TableCell align="right">{totalAmount.toFixed(2)}</TableCell>
                      <TableCell align="left">{currency === 1 ? 'Zim Dollar' : 'USD'}</TableCell>
                      <TableCell align="left">{getStatus({ processState })}</TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => handleEditView({ row })}>
                          <CreateIcon sx={{ color: '#03a9f4' }} />
                        </IconButton>
                      </TableCell>
                      <TableCell align="right">
                        {processState === 0 && (
                          <IconButton
                            onClick={() =>
                              setConfirmDialog({
                                isOpen: true,
                                title: 'Are you sure you want to Delete Batch',
                                subTitle: "You can't undo this action",
                                heading: 'Delete Batch',
                                color: '#00000',
                                onConfirm: () => {
                                  handleBatchDelete({ refId });
                                }
                              })
                            }
                          >
                            <DeleteForeverIcon sx={{ color: '#f44336' }} />
                          </IconButton>
                        )}
                      </TableCell>
                    </StyledTableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
            {isUserNotFound && (
              <TableBody>
                <TableRow>
                  <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                    <SearchNotFound searchQuery={filterName} />
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
        <Controls.Notification notify={notify} setNotify={setNotify} />
        <Controls.ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />

        <TablePagination
          sx={{
            backgroundColor: 'black',
            color: 'white',
            size: 'small'
          }}
          rowsPerPageOptions={[8, 16, 24]}
          component="div"
          count={dataList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
    </Container>
  );
}
